import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost } from '../lib/api';
import Icon from '../components/icons';
import { canOperate } from '../lib/roles';
import RoleBanner from '../components/RoleBanner';

function skillMeta(skill) {
  const meta = skill?.metadata && typeof skill.metadata === 'object' ? skill.metadata : {};
  return {
    description: String(meta.description || '').trim(),
    examplePrompt: String(meta.example_prompt || '').trim(),
  };
}

export default function UserHome() {
  const [skills, setSkills] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    skill_key: '',
    workspace_id: '',
    title: '',
    description: '',
    risk_tier: 1,
  });

  const load = useCallback(async () => {
    try {
      const [skillRows, wsRows] = await Promise.all([
        apiGet('/skills'),
        apiGet('/workspaces'),
      ]);
      const list = (Array.isArray(skillRows) ? skillRows : [])
        .filter((s) => ['enabled', 'reviewed'].includes(String(s.lifecycle || '').toLowerCase()))
        .sort((a, b) => String(a.name || a.key).localeCompare(String(b.name || b.key)));
      const spaces = Array.isArray(wsRows) ? wsRows : [];
      setSkills(list);
      setWorkspaces(spaces);
      setForm((prev) => {
        const skill = list.find((s) => s.key === prev.skill_key) || list[0];
        const ws = spaces.find((w) => String(w.id) === String(prev.workspace_id)) || spaces[0];
        const meta = skillMeta(skill);
        return {
          ...prev,
          skill_key: skill?.key || '',
          workspace_id: ws ? String(ws.id) : '',
          title: prev.title || skill?.name || '',
          description: prev.description || meta.examplePrompt || meta.description || '',
          risk_tier: skill?.risk_tier != null ? Number(skill.risk_tier) : prev.risk_tier,
        };
      });
    } catch (e) {
      setErr(String(e.message || e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function onSkillChange(e) {
    const key = e.target.value;
    const skill = skills.find((s) => s.key === key);
    const meta = skillMeta(skill);
    setForm((prev) => ({
      ...prev,
      skill_key: key,
      title: skill?.name || key,
      description: meta.examplePrompt || meta.description || prev.description,
      risk_tier: skill?.risk_tier != null ? Number(skill.risk_tier) : prev.risk_tier,
    }));
  }

  async function runSkill(e) {
    e.preventDefault();
    setErr('');
    setDone(null);
    if (!form.skill_key || !form.workspace_id) {
      setErr('Pick a skill and workspace.');
      return;
    }
    if (!form.description.trim()) {
      setErr('Describe what you need — Claude uses this as the request.');
      return;
    }
    setBusy(true);
    try {
      const task = await apiPost('/tasks', {
        workspace_id: Number(form.workspace_id),
        customer_id: 1,
        title: form.title || form.skill_key,
        description: form.description.trim(),
        risk_tier: Number(form.risk_tier) || 1,
        routing_mode: 'auto',
        status: 'queued',
        created_by: 'user-home',
      });
      const applied = await apiPost('/route/apply', {
        task_id: task.id,
        workspace_id: Number(form.workspace_id),
        skill_key: form.skill_key,
        min_autonomy: 0,
        risk_tier: Number(form.risk_tier) || 1,
      });
      const needsApproval = Boolean(
        applied?.approval_required || applied?.status === 'awaiting_approval'
      );
      setDone({
        taskId: task.id,
        needsApproval,
        agent: applied?.agent_name || applied?.route?.agent_name,
      });
    } catch (e2) {
      setErr(String(e2.message || e2));
    } finally {
      setBusy(false);
    }
  }

  const selected = skills.find((s) => s.key === form.skill_key);

  return (
    <main className="page user-home">
      <div className="page__header">
        <span className="page__icon"><Icon name="routing" /></span>
        <div className="page__header-text">
          <h2>Run a skill</h2>
          <p>Pick a skill, describe the work, and send it. We’ll route, run Claude, and update Tasks, Slack, and Plane.</p>
        </div>
      </div>

      <ol className="user-home__steps">
        <li><strong>1</strong> Choose skill</li>
        <li><strong>2</strong> Describe request</li>
        <li><strong>3</strong> Send → watch Tasks / Slack / Plane</li>
      </ol>

      <RoleBanner action="run skills" />

      {err && <div className="status status--error">{err}</div>}

      {done && (
        <div className="status status--ok user-home__done">
          <p>
            Task <strong>#{done.taskId}</strong> sent
            {done.agent ? <> to <strong>{done.agent}</strong></> : null}.
            {done.needsApproval
              ? ' Waiting for approval before Claude runs.'
              : ' Claude is running — check Tasks for output.'}
          </p>
          <div className="user-home__done-actions">
            <Link to="/tasks" className="btn-primary">Open My tasks</Link>
            {done.needsApproval && canOperate() && (
              <Link to="/approvals" className="btn-secondary">Go to Approvals</Link>
            )}
          </div>
        </div>
      )}

      {canOperate() ? (
        <section className="panel user-home__panel">
          <form className="user-home__form" onSubmit={runSkill}>
            <label className="field-label">
              Skill
              <select
                className="field-input"
                value={form.skill_key}
                onChange={onSkillChange}
                required
              >
                {skills.length === 0 && <option value="">No skills available</option>}
                {skills.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.name || s.key}
                  </option>
                ))}
              </select>
            </label>

            <label className="field-label">
              Workspace
              <select
                className="field-input"
                value={form.workspace_id}
                onChange={(e) => setForm({ ...form, workspace_id: e.target.value })}
                required
              >
                {workspaces.length === 0 && <option value="">No workspaces</option>}
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name || `Workspace ${w.id}`}
                  </option>
                ))}
              </select>
            </label>

            <label className="field-label">
              Title
              <input
                className="field-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Short label for this run"
                required
              />
            </label>

            <label className="field-label field-label--full">
              Your request
              <textarea
                className="field-input"
                rows={6}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Paste the bug report, brief, or question…"
                required
              />
            </label>

            {selected && (
              <p className="user-home__hint">
                Risk tier {form.risk_tier}
                {Number(form.risk_tier) >= 1
                  ? ' — higher risk may need approval before Claude runs.'
                  : '.'}
              </p>
            )}

            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Sending…' : 'Send to Claude'}
            </button>
          </form>
        </section>
      ) : (
        <div className="status status--muted">
          Your account is view-only. Ask an admin to upgrade you to Operator to run skills.
        </div>
      )}
    </main>
  );
}
